import React, { useState, useEffect, useRef } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import * as api from 'config/api';
import * as useAPI from 'hooks/useAPI';
import { useModal } from 'hooks/useModal';
import { useScroll } from 'hooks/useScroll';
import { UserTags } from 'pages/PersonaChart';
import { formatDate, formatNumber } from 'utils/formatter';
import Icon from 'components/commons/icon/Icon';
import Loading from 'components/commons/loading/Loading';
import { PersonaChartFilterEditModal } from 'components/features/persona-chart/PersonaChartFilterEditModal';
import { WhatIsPersonaModal } from './WhatIsPersonaModal';
import { amountToAmountColor, arrangeDateForChart } from '../utils/util';
import { UserTag, getColor, getInfo, getUnit } from '../utils/personaChart';

// 2024/05/23 MONO側データ不正障害のリカバリ対応のため追加
// memo: 集計対象外期間
const notTargetPeriodInfo = {
  targetDate: ['2024/05/10', '2024/05/11', '2024/05/12', '2024/05/13'],
  updateDate: '2024/05/14',
};

export type ChartType = 'transition' | 'distribution';

type PeriodDropDownType = 1 | 2 | 3 | 4 | 5 | 6;

type PeriodDropDownList = {
  type: PeriodDropDownType;
  label: string;
};

type AssetAmountTransitionData = {
  date: string;
  userChangeRatio: number;
  averageChangeRatio: number | null;
  benchmarkAmount: number;
};

const initialSelectedPeriodFilter = 1;

export const assetFilterArr = (list: UserTags, type: string) => {
  const arrList = Object.values(list).flat();
  if (type === 'transition') {
    // MEMO:初期状態で選択されているタグ：保有している商品(1種)
    const initialInvestmentProductsTag = list.queryTags.includes(2101)
      ? [2101]
      : list.queryTags.includes(2102)
      ? [2102]
      : list.queryTags.includes(2109)
      ? [2109]
      : [];

    return initialInvestmentProductsTag;
  } else {
    // MEMO:初期状態で選択されているタグ：年代のみ
    return arrList.filter(v => v && 1101 <= v && v <= 1110);
  }
};

const prefix = (tag: UserTag) => {
  return tag >= 0 ? '+' : '-';
};

const amountColor = (tag: UserTag) => {
  return tag === 0 ? '' : amountToAmountColor(tag);
};

export const PersonaChartAssetTransition = ({
  userTags,
  user,
  type,
}: {
  userTags: UserTags;
  user: api.User;
  type: ChartType;
}): React.ReactElement => {
  const [assetTransitionData, setAssetTransitionData] = useState<
    AssetAmountTransitionData[] | null
  >(null);
  const [state, setState] = useState<
    useAPI.State<'state', api.GetPersonaAssetTransitionResult>
  >(useAPI.loading);

  const initialSelectedAssetsFilter = assetFilterArr(
    userTags,
    type
  ) as UserTag[];
  const [selectedAssetsFilter, setSelectedAssetsFilter] = useState<UserTag[]>(
    initialSelectedAssetsFilter
  );

  const [selectedPeriodFilter, setSelectedPeriodFilter] = useState<number>(
    initialSelectedPeriodFilter
  );

  useEffect(() => {
    const today = new Date();
    let filterDateFrom = null;

    switch (selectedPeriodFilter) {
      case 2:
        filterDateFrom = new Date(today.setMonth(today.getMonth() - 1));
        break;
      case 3:
        filterDateFrom = new Date(today.setMonth(today.getMonth() - 3));
        break;
      case 4:
        filterDateFrom = new Date(today.setMonth(today.getMonth() - 6));
        break;
      case 5:
        filterDateFrom = new Date(today.setFullYear(today.getFullYear() - 1));
        break;
      case 6:
        filterDateFrom = null;
        break;
      case 1:
      default:
        filterDateFrom = new Date(today.setDate(today.getDate() - 13));
        break;
    }

    const formatDataFrom =
      filterDateFrom === null ? null : formatDate(filterDateFrom, 'yyyy/MM/dd');
    const targetDateFrom =
      formatDataFrom === null
        ? null
        : notTargetPeriodInfo.targetDate.includes(formatDataFrom)
        ? notTargetPeriodInfo.updateDate
        : formatDataFrom;

    const ageTag = selectedAssetsFilter.find(
      num => num !== null && 1101 <= num && num <= 1110
    );
    const annualIncomeTag = selectedAssetsFilter.find(
      num => num !== null && 1300 <= num && num <= 1400
    );
    const assetTag = selectedAssetsFilter.find(
      num => num !== null && 1200 <= num && num <= 1239
    );
    const investmentStartYearTag = selectedAssetsFilter.find(
      num => num !== null && 1500 <= num && num <= 1506
    );
    const investmentProductsTag = selectedAssetsFilter.filter(
      num => num !== null && 2101 <= num && num <= 2202
    );

    const queryTagsList = [
      annualIncomeTag,
      investmentStartYearTag,
      ...investmentProductsTag,
    ].filter(v => v) as number[];

    setState(useAPI.loading);
    api
      .getPersonaAssetTransition({
        ...(ageTag !== undefined ? { AgeTag: ageTag } : {}),
        ...(assetTag !== undefined ? { AssetTag: assetTag } : {}),
        ...(queryTagsList !== undefined ? { QueryTags: queryTagsList } : {}),
        ...(targetDateFrom !== null ? { From: targetDateFrom } : {}),
        ...(selectedPeriodFilter === 1
          ? { To: formatDate(new Date(), 'yyyy/MM/dd') }
          : {}),
      })
      .then(ret => {
        const result = ret as api.GetPersonaAssetTransitionResult;
        const transitionData_ = result.Transitions.map(ret => {
          return {
            date: ret.Date,
            userChangeRatio: ret.UserChangeRatio,
            averageChangeRatio: ret.AverageChangeRatio,
            benchmarkAmount: ret.BenchmarkAmount,
          };
        }) as AssetAmountTransitionData[];

        const transitionData = transitionData_.filter(
          data => !notTargetPeriodInfo.targetDate.includes(data.date)
        );

        setAssetTransitionData(transitionData);
        setState(useAPI.loaded({ state: result }));
      });
  }, [selectedAssetsFilter, selectedPeriodFilter]);

  const [
    isShowPersonaChartFilterEditModal,
    {
      hideModal: hidePersonaChartFilterEditModal,
      showModal: showPersonaChartFilterEditModal,
    },
  ] = useModal();

  const [
    isShowWhaiIsPersonModal,
    { hideModal: hideWhaiIsPersonModal, showModal: showWhaiIsPersonModal },
  ] = useModal();

  const updateAssetsFilter = (tag: UserTag) => {
    const updFilter = selectedAssetsFilter.filter(list => list !== tag);
    setSelectedAssetsFilter(updFilter);
  };

  useScroll();

  if (assetTransitionData === null || useAPI.isLoading(state)) {
    return <ContentLoading title="変動を比べてみる" height="560px" />;
  }

  const assetTransitionItem =
    assetTransitionData[assetTransitionData.length - 1];
  const amountAverage =
    assetTransitionItem.averageChangeRatio === null
      ? null
      : Math.trunc(assetTransitionItem.averageChangeRatio * 10) / 10;
  const amountMyself =
    Math.trunc(assetTransitionItem.userChangeRatio * 10) / 10;
  const amountTopix = Math.trunc(assetTransitionItem.benchmarkAmount * 10) / 10;

  const groupChangeRate =
    assetTransitionItem.averageChangeRatio === null
      ? null
      : Math.trunc(
          (assetTransitionItem.userChangeRatio -
            assetTransitionItem.averageChangeRatio) *
            10
        ) / 10;

  const dayChangeRate =
    Math.trunc(assetTransitionItem.userChangeRatio * 10) / 10;

  let commentType = null;
  if (groupChangeRate === null) {
    commentType = null;
  } else if (
    (groupChangeRate > 0 && dayChangeRate > 0) ||
    (groupChangeRate === 0 && dayChangeRate > 0)
  ) {
    commentType = 1;
  } else if (groupChangeRate < 0 && dayChangeRate > 0) {
    commentType = 2;
  } else if (
    (groupChangeRate === 0 && dayChangeRate === 0) ||
    (groupChangeRate === 0 && dayChangeRate < 0) ||
    (groupChangeRate < 0 && dayChangeRate === 0) ||
    (groupChangeRate > 0 && dayChangeRate === 0) ||
    (groupChangeRate > 0 && dayChangeRate < 0)
  ) {
    commentType = 3;
  } else if (groupChangeRate < 0 && dayChangeRate < 0) {
    commentType = 4;
  }

  return (
    <>
      <div className="pt-8 font-bold">変動を比べてみる</div>
      <div className="mt-2 pt-4 pb-2 px-4 border border-bluegray-300 rounded-t-lg">
        <div className="flex flex-row items-center">
          <div>
            <img
              src="/static/images/persona/persona_icon.svg"
              className=""
              alt="アイコン"
            />
          </div>
          <div className="ml-2 w-full">
            <div className="flex flex-row text-xs text-bluegray-500">
              <div
                className="flex flex-row items-center cursor-pointer"
                onClick={showWhaiIsPersonModal}>
                <div className="">この特徴のグループと比較</div>
                <div className="icon-help_outline ml-1 text-sm"></div>
              </div>
              <div
                className="ml-auto text-blue-700 cursor-pointer"
                onClick={showPersonaChartFilterEditModal}>
                設定変更
              </div>
            </div>
            <div className="flex flex-row flex-wrap">
              {selectedAssetsFilter.length === 0 ? (
                <div className="mt-1 text-bluegray-500 text-xs">
                  絞り込み条件なし
                </div>
              ) : (
                selectedAssetsFilter.map((tag, key) => {
                  if (tag === null) return <></>;
                  const text =
                    1108 <= tag && tag <= 1110
                      ? '80代以上'
                      : getInfo(tag, true) + getUnit(tag);
                  const color = getColor(tag);
                  return (
                    <>
                      <div
                        className={`flex flex-row items-center px-1 py-0.5 mt-2 mr-2 border cursor-pointer ${
                          color !== null
                            ? `${color}`
                            : 'bg-bluegray-150 border-bluegray-300'
                        } rounded`}
                        onClick={() => updateAssetsFilter(tag)}
                        key={key}>
                        <div className="text-xs">{text}</div>
                        <div className="ml-2">
                          <Icon className="text-sm" name="close" />
                        </div>
                      </div>
                    </>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="w-full h-full border-b border-r border-l border-bluegray-300">
        <div className="pt-2 mx-2">
          <Chart
            {...{
              assetTransitionData,
              selectedPeriodFilter,
              setSelectedPeriodFilter,
              amountTopix,
            }}
          />
        </div>
      </div>
      <div className="px-4 pt-4 border-b border-r border-l border-bluegray-300 rounded-b-lg bg-bluegray-700">
        <div className="flex flex-row">
          <div className="w-1/2 text-center text-white border-r border-gray-300">
            <div className="flex justify-center">
              <img
                src="/static/images/persona/persona_icon.svg"
                alt="アイコン"
                style={{ width: '32px', height: '32px' }}
              />
            </div>
            <div
              className={`text-lg font-bold ${amountColor(
                amountAverage === null ? 0 : amountAverage
              )}`}>
              {amountAverage === null ? '' : prefix(amountAverage)}
              {amountAverage === null
                ? '---%'
                : formatNumber(Math.abs(amountAverage), {
                    numberOfDecimal: 1,
                    unitName: '%',
                  })}
            </div>
            <div className="flex flex-row items-center justify-center">
              <div className="">
                <img
                  src="/static/images/persona/legend1.svg"
                  className=""
                  alt="凡例"
                />
              </div>
              <div className="ml-1 text-1.5xs">グループ平均の資産変動率</div>
            </div>
          </div>
          <div className="w-1/2 text-center text-white">
            <div className="flex justify-center">
              {user.ProfileImageURL ? (
                <img
                  src={user.ProfileImageURL}
                  className="rounded-full"
                  alt="プロフィール"
                  style={{ width: '32px', height: '32px' }}
                />
              ) : (
                <div
                  className="icon-person text-3xl border rounded-full border-gray-700 flex justify-center items-center"
                  style={{ width: '32px', height: '32px' }}></div>
              )}
            </div>
            <div className={`text-lg font-bold ${amountColor(amountMyself)}`}>
              {prefix(amountMyself)}
              {formatNumber(Math.abs(amountMyself), {
                numberOfDecimal: 1,
                unitName: '%',
              })}
            </div>
            <div className="flex flex-row items-center justify-center">
              <div className="">
                <img
                  src="/static/images/persona/legend2.svg"
                  className=""
                  alt="凡例"
                />
              </div>
              <div className="ml-1 text-1.5xs">あなたの資産変動率</div>
            </div>
          </div>
        </div>
        <div className="mt-4 py-4 px-4 rounded-t-lg bg-white text-sm">
          <div className="grid" style={{ gridTemplateColumns: 'auto 1fr' }}>
            <div className="flex items-center">
              <img
                src={`/static/images/persona/persona_image${
                  commentType === null ? 4 : commentType
                }.svg`}
                className=""
                style={{ width: '60px', height: '60px' }}
                alt="アイコン"
              />
            </div>
            <div className="flex mx-auto items-center">
              <div className="font-bold">
                あなたの資産は、
                {(() => {
                  switch (selectedPeriodFilter) {
                    case 1:
                      return '14日前';
                    case 2:
                      return '1ヶ月前';
                    case 3:
                      return '3ヶ月前';
                    case 4:
                      return '6ヶ月前';
                    case 5:
                      return '1年前';
                    case 6:
                      return '全期間';
                  }
                })()}
                から
                {dayChangeRate === 0 ? (
                  <>
                    <span>変わりありません。</span>
                  </>
                ) : (
                  <>
                    <span className={`${amountColor(dayChangeRate)}`}>
                      {Math.abs(dayChangeRate)}％
                      {dayChangeRate > 0 ? '増えています。' : '減っています。'}
                    </span>
                  </>
                )}
                {groupChangeRate === null ? (
                  <></>
                ) : (
                  <>
                    グループと比べると、
                    {groupChangeRate === 0 ? (
                      <>
                        <span>変わらない</span>
                      </>
                    ) : (
                      <>
                        <span className={`${amountColor(groupChangeRate)}`}>
                          {Math.abs(groupChangeRate)}％
                          {groupChangeRate > 0
                            ? '上回っている'
                            : '下回っている'}
                        </span>
                      </>
                    )}
                    ようです。
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="mt-2">
            {(() => {
              switch (commentType) {
                case null:
                  return (
                    <>
                      資産運用には我慢の時も必要です。
                      <br />
                      資産を増やしている人が何に投資をしているか調べてみると、発見があるかもしれません。
                      <br />
                      グループの特徴を変えて、比較してみましょう！
                    </>
                  );
                case 1:
                  return '上手に資産運用できているようですね。素晴らしい！';
                case 2:
                  return (
                    <>
                      しっかり資産を増やせていますね！
                      <br />
                      さらに資産を増やしている人が何に投資をしているか調べてみると、発見があるかもしれません。
                      <br />
                      グループの特徴を変えて、比較してみましょう！
                    </>
                  );
                case 3:
                  return (
                    <>
                      大丈夫、あせらず冷静な判断を心がけてください。
                      <br />
                      他にどんなグループが資産を増やしているか、比較してみましょう！
                    </>
                  );
                case 4:
                  return (
                    <>
                      資産運用には我慢の時も必要です。
                      <br />
                      資産を増やしている人が何に投資をしているか調べてみると、発見があるかもしれません。
                      <br />
                      グループの特徴を変えて、比較してみましょう！
                    </>
                  );
                default:
                  return <></>;
              }
            })()}
          </div>
        </div>
      </div>

      <PersonaChartFilterEditModal
        hide={hidePersonaChartFilterEditModal}
        isShow={isShowPersonaChartFilterEditModal}
        chartAssetFilter={selectedAssetsFilter}
        setChartAssetFilter={setSelectedAssetsFilter}
        type="AssetsAmount"
      />
      <WhatIsPersonaModal
        hide={hideWhaiIsPersonModal}
        isShow={isShowWhaiIsPersonModal}
      />
    </>
  );
};

const chartDataFormat = (data: AssetAmountTransitionData[], type: string) => {
  return data.map(
    (data): Highcharts.PointOptionsObject => {
      return {
        x: arrangeDateForChart(new Date(data.date)).getTime(),
        y:
          type === 'topix'
            ? data.benchmarkAmount
            : type === 'average'
            ? data.averageChangeRatio
            : data.userChangeRatio,
      };
    }
  );
};

const Chart = ({
  assetTransitionData,
  selectedPeriodFilter,
  setSelectedPeriodFilter,
  amountTopix,
}: {
  assetTransitionData: AssetAmountTransitionData[];
  selectedPeriodFilter: number;
  setSelectedPeriodFilter: React.Dispatch<React.SetStateAction<number>>;
  amountTopix: number;
}): React.ReactElement => {
  const selectedPeriodFilterList = [
    {
      type: 1,
      label: '14日間',
    },
    {
      type: 2,
      label: '1ヶ月',
    },
    {
      type: 3,
      label: '3ヶ月',
    },
    {
      type: 4,
      label: '6ヶ月',
    },
    {
      type: 5,
      label: '1年',
    },
    // MEMO: 一時的に全期間を選択肢から非表示にする
    // {
    //   type: 6,
    //   label: '全期間',
    // },
  ] as PeriodDropDownList[];

  const selectedPeriodFilterStates = selectedPeriodFilterList.map(
    list => list.type
  );
  const selectedPeriodFilterLabel = (type: number) => {
    const info = selectedPeriodFilterList.find(list => type === list.type);
    return info === undefined ? '' : info.label;
  };

  const dateFormatter = (date: Date, selectedPeriodFilter: number) => {
    if (selectedPeriodFilter === 1 || selectedPeriodFilter === 2) {
      return `${date.getMonth() + 1}/${date.getDate()}`;
    } else {
      return `${date.getFullYear()}/${date.getMonth() + 1}`;
    }
  };

  const toString = (value: number) => `${value.toString()}`;
  const fromString = (str: string) => {
    const num = Number(str);
    return num as number;
  };

  const [options, setOptions] = useState<Highcharts.Options>({
    chart: {
      animation: {
        duration: 0,
      },
      height: 204,
      marginBottom: 20,
      backgroundColor: '#ffffff',
    },
    credits: {
      enabled: false,
    },
    exporting: { enabled: false },
    rangeSelector: {
      enabled: false,
      inputEnabled: false,
    },
    yAxis: {
      allowDecimals: true,
      labels: {
        align: 'left',
        x: 5,
        y: 4,
        formatter(): string {
          return `${this.value}%`;
        },
      },
      title: {
        text: null,
      },
      opposite: true,
      plotLines: [
        {
          color: '#9fa2a5',
          width: 2,
          value: 0,
        },
      ],
    },
    legend: {
      enabled: false,
    },
    navigator: {
      enabled: false,
    },
    tooltip: {
      borderColor: 'transparent',
      useHTML: true,
      outside: true,
      shared: true,
      formatter: function() {
        return renderToStaticMarkup(
          <Tooltip date={new Date(this.x)}>
            {(this.points ?? []).map(point => (
              <TooltipItem
                name={point.series.name}
                color={point.color.toString()}
                value={point.y}
              />
            ))}
          </Tooltip>
        );
      },
    },
    title: {
      text: '',
      style: { display: 'none' },
    },
    plotOptions: {
      area: {
        fillColor: {
          linearGradient: {
            x1: 0,
            y1: 0,
            x2: 0,
            y2: 1,
          },
          stops: [
            [0, 'rgba(148, 163, 184, 0.3)'],
            [1, 'rgba(250, 251, 252, 0.3)'],
          ],
        },
        threshold: null,
        turboThreshold: 15000,
      },
    },
  });

  useEffect(() => {
    const tickInterval =
      selectedPeriodFilter === 1
        ? 2 * 24 * 3600 * 1000
        : selectedPeriodFilter === 2
        ? 7 * 24 * 3600 * 1000
        : selectedPeriodFilter === 3
        ? 30 * 24 * 3600 * 1000
        : selectedPeriodFilter === 4
        ? 60 * 24 * 3600 * 1000
        : selectedPeriodFilter === 5
        ? 90 * 24 * 3600 * 1000
        : 180 * 24 * 3600 * 1000;
    setOptions(prev => ({
      ...prev,
      series: [
        {
          name: 'TOPIX',
          type: 'area',
          data: chartDataFormat(assetTransitionData, 'topix'),
          color: '#94A3B8',
          fillOpacity: 0.3,
          marker: {
            enabled: false,
          },
        },
        {
          name: 'グループ平均',
          type: 'line',
          data: chartDataFormat(assetTransitionData, 'average'),
          color: '#14B8A6',
          marker: {
            enabled: selectedPeriodFilter === 1 ? true : false,
            symbol: 'circle',
            radius: 4,
            lineWidth: 3,
            lineColor: '#14B8A6',
            fillColor: '#ffffff',
          },
        },
        {
          name: 'あなた',
          type: 'line',
          data: chartDataFormat(assetTransitionData, 'origin'),
          color: '#FB923C',
          marker: {
            enabled: selectedPeriodFilter === 1 ? true : false,
            symbol: 'circle',
            radius: 4,
            lineWidth: 3,
            lineColor: '#FB923C',
            fillColor: '#ffffff',
          },
        },
      ],
      xAxis: {
        gridLineWidth: 0,
        title: { style: { color: '#000000' } },
        type: 'datetime',
        labels: {
          formatter(): string {
            if (typeof this.value === 'string') return this.value;
            return dateFormatter(new Date(this.value), selectedPeriodFilter);
          },
        },
        tickInterval: tickInterval,
      },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetTransitionData]);

  const ref = useRef<HighchartsReact.RefObject>(null);
  return (
    <>
      <HighchartsReact highcharts={Highcharts} options={options} ref={ref} />
      <div className="flex flex-row items-center mb-4">
        <div
          className={`relative flex flex-row overflow-hidden w-full items-center rounded border h-8 mt-3 ml-2 border-bluegray-400`}
          style={{ width: '125px' }}>
          <select
            className={`w-full h-full appearance-none bg-transparent text-xs pl-3 cursor-pointer`}
            value={selectedPeriodFilter}
            onChange={(e): void => {
              const result = fromString(e.currentTarget.value);
              setSelectedPeriodFilter(result);
            }}>
            {selectedPeriodFilterStates.map((s, i) => {
              return (
                <option key={i} value={toString(s)}>
                  {selectedPeriodFilterLabel(s)}
                </option>
              );
            })}
          </select>
          <div
            className={'absolute pointer-events-none flex items-center pr-1'}
            style={{ right: '6%' }}>
            <i
              className={`text-bluegray-400 cursor-pointer icon-chevron-down p-0.5 ml-auto`}
            />
          </div>
        </div>
        <div className="flex flex-row items-center mt-4 ml-auto mr-4 text-xs">
          <img
            src="/static/images/persona/legend3.svg"
            className=""
            alt="凡例"
          />
          <div className="ml-1">TOPIX</div>
          <div
            className={`flex items-center justify-center ml-2 ${amountColor(
              amountTopix
            )}`}>
            {prefix(amountTopix)}
            {formatNumber(Math.abs(amountTopix), {
              numberOfDecimal: 1,
              unitName: '%',
            })}
          </div>
        </div>
      </div>
    </>
  );
};

const Tooltip = ({
  date,
  children,
}: {
  date?: Date;
  children: React.ReactNodeArray;
}) => {
  return (
    <>
      <div
        style={{
          display: 'grid',
          gap: '2px',
          gridAutoRows: 'min-content',
          gridAutoFlow: 'row',
        }}>
        {date && (
          <div style={{ paddingBottom: '3px', fontSize: '9px' }}>
            {date.getFullYear()}/{date.getMonth() + 1}/{date.getDate()}
          </div>
        )}
        {children}
      </div>
    </>
  );
};

const TooltipItem = ({
  name,
  color,
  value,
}: {
  name: string;
  color: string;
  value: number;
}) => {
  return (
    <>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'min-content min-content min-content',
          placeItems: 'center start',
          gap: '2px',
        }}>
        <div
          style={{
            borderRadius: '50%',
            height: '5px',
            width: '5px',
            gridColumn: 1,
            backgroundColor: color,
          }}
        />
        <span style={{ fontSize: '10px', gridColumn: 2 }}>{name}:</span>
        <span
          style={{
            fontWeight: 'bold',
            fontSize: '10px',
            gridColumn: 3,
          }}>
          {formatNumber(value, { numberOfDecimal: 1 })}%
        </span>
      </div>
    </>
  );
};

export const ContentLoading = ({
  title,
  height,
}: {
  title: string;
  height: string;
}) => {
  return (
    <>
      <div className="pt-8 font-bold">{title}</div>
      <div
        className="flex justify-center mt-2 border border-bluegray-300 rounded-lg"
        style={{ height: `${height}` }}>
        <Loading circularLoading={true} loading />
      </div>
    </>
  );
};
